import * as bpac from "./bpac.js"

// Types for better type safety
export interface GuestData {
    visitorName: string
    visitorCompany: string
    hostName: string
}

export interface GroupData {
    groupName: string
    groupSize: number
    hostName: string
}

export interface TakeawayLabelData {
    orderId: string | number
    customerName: string
    deliveryDate: string
    numberOfItems: number
    description: string // comma-separated list of items
    copies?: number
}

export const B_PACK_EXTENSION_URL = "https://chrome.google.com/webstore/detail/ilpghlfadkjifilabejhhijpfphfcfhb"

export class BrotherPrinterService {
    private static readonly DATA_FOLDER = "https://facilitynet.dk/remote/labelTemplates/"

    /**
     * Check if Brother b-PAC extension is installed
     */
    static checkExtensionInstalled(): boolean {
        if (!bpac.IsExtensionInstalled()) {
            return false
        }
        return true
    }

    /**
     * Generate a random 5-digit control code
     */
    private static generateControlCode(): number {
        return Math.floor(Math.random() * 90000) + 10000
    }

    /**
     * Get current date in DD.MM.YYYY format
     */
    private static getCurrentDate(): string {
        const now = new Date()
        const day = now.getDate().toString().padStart(2, "0")
        const month = (now.getMonth() + 1).toString().padStart(2, "0")
        const year = now.getFullYear()
        return `${day}.${month}.${year}`
    }

    /**
     * Find the template file to use (custom or default)
     */
    private static async getTemplatePath(folderName?: string): Promise<string> {
        if (!folderName) {
            return `${this.DATA_FOLDER}brother-ql-820nwb-62mm_default.lbx`
        }

        const customFilePath = `${this.DATA_FOLDER}${folderName}/brother-ql-820nwb-62mm_custom.lbx`
        const defaultFilePath = `${this.DATA_FOLDER}brother-ql-820nwb-62mm_default.lbx`

        try {
            const response = await fetch(customFilePath, { method: "HEAD" })
            return response.ok ? customFilePath : defaultFilePath
        } catch (error) {
            console.warn("Could not check custom template:", error)
            return defaultFilePath
        }
    }

    /**
     * Get the takeaway template file path
     */
    private static getTakeawayTemplatePath(): string {
        return `${this.DATA_FOLDER}take-away/TAKE_AWAY.lbx`
    }

    /**
     * Find an online printer
     */
    private static async findOnlinePrinter(): Promise<string | null> {
        const printer = new bpac.IPrinter()
        const installedPrinters = await printer.GetInstalledPrinters()

        for (const printerName of installedPrinters) {
            const isOnline = await printer.IsPrinterOnline(printerName)
            if (isOnline) {
                console.log("Found online printer:", printerName)
                return printerName
            }
        }

        console.error("No online printers found")
        return null
    }

    /**
     * Wake up the printer by querying its status
     */
    static async wakePrinter(): Promise<boolean> {
        try {
            const printer = new bpac.IPrinter()
            const installedPrinters = await printer.GetInstalledPrinters()
            console.log("Installed printers:", installedPrinters)

            for (const printerName of installedPrinters) {
                const isOnline = await printer.IsPrinterOnline(printerName)
                if (isOnline) {
                    console.log("Printer is online:", printerName)
                    // Query media info to wake up the printer
                    const mediaName = await printer.GetMediaName()
                    console.log(`Printer media: ${mediaName}`)
                    return true
                }
            }
            return false
        } catch (error) {
            console.error("Error waking up printer:", error)
            return false
        }
    }

    /**
     * Print a single guest label
     */
    static async printGuestLabel(guestData: GuestData, folderName?: string): Promise<boolean> {
        try {
            // Check if extension is installed
            if (!this.checkExtensionInstalled()) {
                throw new Error("Brother b-PAC extension is not installed")
            }

            // Get template path
            const templatePath = await this.getTemplatePath(folderName)
            console.log("Using template:", templatePath)

            // Open the label template
            const document = await bpac.IDocument.Open(templatePath)
            if (!document) {
                throw new Error("Failed to open label template")
            }

            // Generate label data
            const controlCode = this.generateControlCode()
            const validToDate = this.getCurrentDate()

            // Set label fields
            const objHeader = await bpac.IDocument.GetObject("HEADER")
            objHeader.Text = "Gæstekort"

            const objVisitor = await bpac.IDocument.GetObject("VISITOR")
            objVisitor.Text = guestData.visitorName

            const objCompany = await bpac.IDocument.GetObject("COMPANY")
            objCompany.Text = guestData.visitorCompany

            const objHost = await bpac.IDocument.GetObject("HOST")
            objHost.Text = guestData.hostName

            const objValidTo = await bpac.IDocument.GetObject("VALIDTO")
            objValidTo.Text = validToDate

            const objControlCode = await bpac.IDocument.GetObject("CODE")
            objControlCode.Text = controlCode.toString()

            // Find online printer
            const printerName = await this.findOnlinePrinter()
            if (!printerName) {
                throw new Error("No online printer found")
            }

            // Print the label
            await bpac.IDocument.SetPrinter(printerName)
            await bpac.IDocument.StartPrint("", 0)
            await bpac.IDocument.PrintOut(1, 0)
            await bpac.IDocument.EndPrint()
            await bpac.IDocument.Close()

            console.log("Guest label printed successfully")
            return true
        } catch (error) {
            console.error("Error printing guest label:", error)
            return false
        }
    }

    /**
     * Print multiple labels for a group
     */
    static async printGroupLabels(groupData: GroupData, folderName?: string): Promise<boolean> {
        try {
            // Check if extension is installed
            if (!this.checkExtensionInstalled()) {
                throw new Error("Brother b-PAC extension is not installed")
            }

            // Get template path
            const templatePath = await this.getTemplatePath(folderName)
            console.log("Using template:", templatePath)

            // Open the label template
            const document = await bpac.IDocument.Open(templatePath)
            if (!document) {
                throw new Error("Failed to open label template")
            }

            // Generate label data
            const controlCode = this.generateControlCode()
            const validToDate = this.getCurrentDate()

            // Find online printer
            const printerName = await this.findOnlinePrinter()
            if (!printerName) {
                throw new Error("No online printer found")
            }

            // Print labels for each guest in the group
            for (let i = 0; i < groupData.groupSize; i++) {
                console.log(`Printing guest card ${i + 1} of ${groupData.groupSize}`)

                // Set label fields for this guest
                const objHeader = await bpac.IDocument.GetObject("HEADER")
                objHeader.Text = "Gæstekort"

                const objVisitor = await bpac.IDocument.GetObject("VISITOR")
                objVisitor.Text = groupData.groupName

                const objCompany = await bpac.IDocument.GetObject("COMPANY")
                objCompany.Text = `Gæst #${i + 1}`

                const objHost = await bpac.IDocument.GetObject("HOST")
                objHost.Text = groupData.hostName

                const objValidTo = await bpac.IDocument.GetObject("VALIDTO")
                objValidTo.Text = validToDate

                const objControlCode = await bpac.IDocument.GetObject("CODE")
                objControlCode.Text = controlCode.toString()

                // Print this label
                await bpac.IDocument.SetPrinter(printerName)
                await bpac.IDocument.StartPrint("", 0)
                await bpac.IDocument.PrintOut(1, 0)
                await bpac.IDocument.EndPrint()
            }

            await bpac.IDocument.Close()
            console.log(`Successfully printed ${groupData.groupSize} group labels`)
            return true
        } catch (error) {
            console.error("Error printing group labels:", error)
            return false
        }
    }

    /**
     * Print multiple takeaway order labels
     */
    static async printMultipleTakeawayLabels(labelDataList: TakeawayLabelData[]): Promise<boolean> {
        try {
            // Check if extension is installed
            if (!this.checkExtensionInstalled()) {
                throw new Error("Brother b-PAC extension is not installed")
            }

            // Get takeaway template path
            const templatePath = this.getTakeawayTemplatePath()
            console.log("Using takeaway template:", templatePath)

            // Open the label template
            const document = await bpac.IDocument.Open(templatePath)
            if (!document) {
                throw new Error("Failed to open takeaway label template")
            }

            // Find online printer
            const printerName = await this.findOnlinePrinter()
            if (!printerName) {
                throw new Error("No online printer found")
            }

            // Print labels for each order
            for (let i = 0; i < labelDataList.length; i++) {
                const labelData = labelDataList[i]
                console.log(`Printing takeaway label ${i + 1} of ${labelDataList.length}`)

                // Set label fields for this order
                const objOrderNumber = await bpac.IDocument.GetObject("ORDER_NUMBER")
                if (objOrderNumber) objOrderNumber.Text = `#${labelData.orderId}`

                const objName = await bpac.IDocument.GetObject("NAME")
                if (objName) objName.Text = labelData.customerName

                const objDate = await bpac.IDocument.GetObject("DATE")
                if (objDate) objDate.Text = labelData.deliveryDate

                const objNumberOfItems = await bpac.IDocument.GetObject("NUMBER_OF_ITEMS")
                if (objNumberOfItems) objNumberOfItems.Text = `${labelData.numberOfItems} items`

                const objDescription = await bpac.IDocument.GetObject("DESCRIPTION")
                if (objDescription) objDescription.Text = labelData.description

                // Print this label
                const copies = labelData.copies ?? 1
                await bpac.IDocument.SetPrinter(printerName)
                await bpac.IDocument.StartPrint("", 0)
                await bpac.IDocument.PrintOut(copies, 0)
                await bpac.IDocument.EndPrint()
            }

            await bpac.IDocument.Close()
            console.log(`Successfully printed ${labelDataList.length} takeaway labels`)
            return true
        } catch (error) {
            console.error("Error printing takeaway labels:", error)
            return false
        }
    }
}
